import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const PageChromeContext = createContext(null);

/**
 * Provides the current section title for the top navbar.
 * Pages can override with usePageTitle(); otherwise Layout derives from the route.
 */
export function PageChromeProvider({ children }) {
  const [override, setOverride] = useState(null);

  const setPageTitle = useCallback((title) => {
    setOverride(title == null || title === '' ? null : String(title));
  }, []);

  const clearPageTitle = useCallback(() => setOverride(null), []);

  const value = useMemo(
    () => ({
      overrideTitle: override,
      setPageTitle,
      clearPageTitle,
    }),
    [override, setPageTitle, clearPageTitle]
  );

  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export function usePageChrome() {
  const ctx = useContext(PageChromeContext);
  if (!ctx) {
    throw new Error('usePageChrome must be used within PageChromeProvider');
  }
  return ctx;
}

/**
 * Sets the top-bar section title while this component is mounted.
 * Pass null/undefined to fall back to the route-derived label.
 */
export function usePageTitle(title) {
  const { setPageTitle, clearPageTitle } = usePageChrome();

  useEffect(() => {
    if (title == null || title === '') {
      clearPageTitle();
      return undefined;
    }
    setPageTitle(title);
    return () => clearPageTitle();
  }, [title, setPageTitle, clearPageTitle]);
}
